/**
 * Employee contract — mirrors the `platform.employees` Drizzle table.
 *
 * Single source of truth for the API boundary. The server parses request
 * bodies with `NewEmployeeSchema` / `UpdateEmployeeSchema` and returns
 * rows shaped like `EmployeeSchema`. Clients import these schemas to
 * validate forms and type their API responses.
 *
 * The enum schemas (`EmploymentTypeSchema`, etc.) correspond exactly to
 * the CHECK constraints in migration `0002_employees.sql` — keep them
 * aligned by hand.
 */
import { z } from 'zod';

import { TenantIdSchema, UserIdSchema } from '../common/identifiers.js';
import { AuditFieldsSchema } from '../common/timestamps.js';

/** UUID-branded identifier for an employee row. */
export const EmployeeIdSchema = z.string().uuid().brand<'EmployeeId'>();
export type EmployeeId = z.infer<typeof EmployeeIdSchema>;

// ---------------------------------------------------------------------------
// Enum fields
// ---------------------------------------------------------------------------

export const GenderSchema = z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say']);
export type Gender = z.infer<typeof GenderSchema>;

export const MaritalStatusSchema = z.enum(['single', 'married', 'divorced', 'widowed']);
export type MaritalStatus = z.infer<typeof MaritalStatusSchema>;

export const EmploymentTypeSchema = z.enum([
  'full_time',
  'part_time',
  'contract',
  'intern',
  'consultant',
]);
export type EmploymentType = z.infer<typeof EmploymentTypeSchema>;

export const EmploymentStatusSchema = z.enum([
  'probation',
  'confirmed',
  'notice',
  'separated',
  'terminated',
]);
export type EmploymentStatus = z.infer<typeof EmploymentStatusSchema>;

export const WorkLocationTypeSchema = z.enum(['office', 'wfh', 'hybrid']);
export type WorkLocationType = z.infer<typeof WorkLocationTypeSchema>;

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** ISO calendar date, wire format `YYYY-MM-DD`. */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected ISO date YYYY-MM-DD');

const EmailLikeSchema = z.string().email().max(320);

// ---------------------------------------------------------------------------
// Employee record (output)
// ---------------------------------------------------------------------------

/**
 * Full employee record as returned by the API. Extends `AuditFieldsSchema`
 * so every row carries `createdAt`/`updatedAt`/`createdBy`/`updatedBy`/
 * `deletedAt`.
 */
export const EmployeeSchema = AuditFieldsSchema.extend({
  id: EmployeeIdSchema,
  tenantId: TenantIdSchema,
  userId: UserIdSchema.nullable(),

  employeeCode: z.string().min(1).max(50),
  displayName: z.string().min(1).max(200),
  firstName: z.string().min(1).max(100),
  middleName: z.string().max(100).nullable(),
  lastName: z.string().min(1).max(100),
  preferredName: z.string().max(100).nullable(),

  workEmail: EmailLikeSchema,
  personalEmail: EmailLikeSchema.nullable(),
  workPhone: z.string().max(32).nullable(),
  personalPhone: z.string().max(32).nullable(),

  dateOfBirth: IsoDateSchema.nullable(),
  gender: GenderSchema.nullable(),
  nationality: z.string().max(80).nullable(),
  bloodGroup: z.string().max(8).nullable(),
  maritalStatus: MaritalStatusSchema.nullable(),

  employmentType: EmploymentTypeSchema,
  employmentStatus: EmploymentStatusSchema,
  joiningDate: IsoDateSchema,
  probationEndDate: IsoDateSchema.nullable(),
  confirmationDate: IsoDateSchema.nullable(),
  separationDate: IsoDateSchema.nullable(),

  jobTitle: z.string().max(150).nullable(),
  // FK stubs — Wave 1.3 will tighten these to branded IDs.
  departmentId: z.string().uuid().nullable(),
  designationId: z.string().uuid().nullable(),
  reportingManagerId: EmployeeIdSchema.nullable(),
  workLocationId: z.string().uuid().nullable(),
  workLocationType: WorkLocationTypeSchema.nullable(),

  customFields: z.record(z.string(), z.unknown()).default({}),
});
export type Employee = z.infer<typeof EmployeeSchema>;

// ---------------------------------------------------------------------------
// Create / update (input)
// ---------------------------------------------------------------------------

/**
 * Input shape for `POST /employees`. Drops server-managed audit fields,
 * `tenantId` (derived from `req.tenant`), and `id` (generated).
 * `employmentStatus` defaults to 'probation' server-side.
 */
export const NewEmployeeSchema = z.object({
  userId: UserIdSchema.nullable().optional(),

  employeeCode: z.string().min(1).max(50),
  displayName: z.string().min(1).max(200),
  firstName: z.string().min(1).max(100),
  middleName: z.string().max(100).nullable().optional(),
  lastName: z.string().min(1).max(100),
  preferredName: z.string().max(100).nullable().optional(),

  workEmail: EmailLikeSchema,
  personalEmail: EmailLikeSchema.nullable().optional(),
  workPhone: z.string().max(32).nullable().optional(),
  personalPhone: z.string().max(32).nullable().optional(),

  dateOfBirth: IsoDateSchema.nullable().optional(),
  gender: GenderSchema.nullable().optional(),
  nationality: z.string().max(80).nullable().optional(),
  bloodGroup: z.string().max(8).nullable().optional(),
  maritalStatus: MaritalStatusSchema.nullable().optional(),

  employmentType: EmploymentTypeSchema,
  employmentStatus: EmploymentStatusSchema.optional(),
  joiningDate: IsoDateSchema,
  probationEndDate: IsoDateSchema.nullable().optional(),
  confirmationDate: IsoDateSchema.nullable().optional(),
  separationDate: IsoDateSchema.nullable().optional(),

  jobTitle: z.string().max(150).nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  designationId: z.string().uuid().nullable().optional(),
  reportingManagerId: EmployeeIdSchema.nullable().optional(),
  workLocationId: z.string().uuid().nullable().optional(),
  workLocationType: WorkLocationTypeSchema.nullable().optional(),

  customFields: z.record(z.string(), z.unknown()).optional(),
});
export type NewEmployee = z.infer<typeof NewEmployeeSchema>;

/**
 * Input shape for `PATCH /employees/:id`. Every field optional; the service
 * applies only the fields actually supplied (no "accidental null wipe" from
 * a field being omitted).
 */
export const UpdateEmployeeSchema = NewEmployeeSchema.partial();
export type UpdateEmployee = z.infer<typeof UpdateEmployeeSchema>;
